/**
 * Backlog — dev backlog board. Marcin tracks tickets; bosses (admin/management) add
 * requests, test each feature on staging (test.fullframe.nl), and open/review/merge its
 * PR in-app. Data via /Api/backlog (admin-gated). Board modelled on TaskBoard's dnd-kit.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Tooltip, CircularProgress, Stack, Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScienceIcon from '@mui/icons-material/Science';
import MergeIcon from '@mui/icons-material/CallMerge';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import PageHeader from '../../ui-components/PageHeader';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';

const API = `${BACKEND_URL}/Api/backlog`;
const STAGING = 'https://test.fullframe.nl';

const COLUMNS = [
  { key: 'backlog', label: 'Backlog', color: '#868e96' },
  { key: 'in_progress', label: 'Mee bezig', color: '#4c6ef5' },
  { key: 'in_review', label: 'In review (PR)', color: '#f59f00' },
  { key: 'testing', label: 'Testen op staging', color: '#7048e8' },
  { key: 'done', label: 'Klaar', color: '#37b24d' },
];
const PRIO = { low: { l: 'Laag', c: '#adb5bd' }, med: { l: 'Middel', c: '#4dabf7' }, high: { l: 'Hoog', c: '#ff6b6b' } };
const prColor = (s) =>
  ['success', 'approved', 'merged'].includes(s) ? '#37b24d'
  : ['failure', 'changes_requested'].includes(s) ? '#fa5252'
  : ['pending', 'open', 'draft'].includes(s) ? '#f59f00' : '#868e96';

async function okJson(r) {
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
  return d;
}
const jsonOpts = (method, body) => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const api = {
  list: () => authFetch(API).then(okJson).then((d) => (Array.isArray(d) ? d : [])),
  create: (b) => authFetch(API, jsonOpts('POST', b)).then(okJson),
  update: (id, b) => authFetch(`${API}/${id}`, jsonOpts('PATCH', b)).then(okJson),
  del: (id) => authFetch(`${API}/${id}`, { method: 'DELETE' }).then(okJson),
  comments: (id) => authFetch(`${API}/${id}/comments`).then((r) => leesJson(r, 'Comments laden')),
  addComment: (id, body) => authFetch(`${API}/${id}/comments`, jsonOpts('POST', { body })).then(okJson),
  openPr: (id) => authFetch(`${API}/${id}/pr`, { method: 'POST' }).then(okJson),
  refreshPr: (id) => authFetch(`${API}/${id}/pr`).then(okJson),
  mergePr: (id) => authFetch(`${API}/${id}/pr/merge`, { method: 'POST' }).then(okJson),
};

/* ── card ── */
function Card({ t, onOpen, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: String(t.id) });
  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.4 : 1,
  };
  const prio = PRIO[t.priority] || PRIO.med;
  return (
    <Paper ref={setNodeRef} style={style} {...listeners} {...attributes} elevation={0}
      onClick={() => onOpen(t)}
      sx={{ p: 1.25, mb: 1, borderRadius: 2, border: '1px solid #e4e7ec', cursor: 'grab',
        '&:hover': { borderColor: '#c3c9d4', boxShadow: '0 2px 8px rgba(0,0,0,.06)' } }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 650, lineHeight: 1.3, mb: 0.5 }}>{t.title}</Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
        <Chip size="small" label={prio.l} sx={{ height: 18, fontSize: 10.5, bgcolor: prio.c + '22', color: prio.c }} />
        {t.pr_number && (
          <Chip size="small" label={`PR #${t.pr_number} · ${t.pr_state || '?'}`}
            sx={{ height: 18, fontSize: 10.5, bgcolor: prColor(t.pr_state) + '22', color: prColor(t.pr_state) }} />
        )}
        {t.pr_checks && t.pr_checks !== 'none' && (
          <Chip size="small" label={`checks: ${t.pr_checks}`} sx={{ height: 18, fontSize: 10, bgcolor: prColor(t.pr_checks) + '22', color: prColor(t.pr_checks) }} />
        )}
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontSize: 11, color: '#868e96' }}>
          {t.assignee ? t.assignee.split('@')[0] : '—'}{t.comment_count ? ` · 💬 ${t.comment_count}` : ''}
        </Typography>
        <Stack direction="row" spacing={0}>
          {(t.status === 'testing' || t.status === 'done') && (
            <Tooltip title="Test op staging">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); window.open(STAGING + (t.test_path || '/'), '_blank'); }}>
                <ScienceIcon sx={{ fontSize: 15, color: '#7048e8' }} />
              </IconButton>
            </Tooltip>
          )}
          {/* Verwijderen direct vanaf de kaart (wiki-feedback 'Feedback verwerken', 9 sep) — met bevestiging. */}
          {onDelete && (
            <Tooltip title="Verwijderen">
              <IconButton size="small" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onDelete(t); }}>
                <DeleteOutlineIcon sx={{ fontSize: 15, color: '#adb5bd', '&:hover': { color: '#e03131' } }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

/* ── column ── */
function Column({ col, tickets, onOpen, onDelete }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key });
  return (
    <Box ref={setNodeRef} sx={{ flex: '1 1 0', minWidth: 220, bgcolor: isOver ? '#eef2ff' : '#f6f7f9',
      borderRadius: 2, p: 1, transition: 'background .12s' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5, mb: 1 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: col.color }} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#3d4351' }}>{col.label}</Typography>
        <Typography sx={{ fontSize: 11.5, color: '#adb5bd' }}>{tickets.length}</Typography>
      </Stack>
      {tickets.map((t) => <Card key={t.id} t={t} onOpen={onOpen} onDelete={onDelete} />)}
    </Box>
  );
}

/* ── create / edit dialog ── */
const EMPTY = { title: '', description: '', acceptance_criteria: '', priority: 'med', assignee: '', branch: '', base_branch: 'develop', test_path: '' };
function TicketForm({ open, initial, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (initial) setF({ ...EMPTY, ...initial, acceptance_criteria: (initial.acceptance_criteria || []).join('\n') });
    else setF(EMPTY);
  }, [initial, open]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async () => {
    if (!f.title.trim()) { toast.error('Titel is verplicht'); return; }
    setSaving(true);
    const body = { ...f, acceptance_criteria: f.acceptance_criteria.split('\n').map((s) => s.trim()).filter(Boolean) };
    try {
      const saved = initial?.id ? await api.update(initial.id, body) : await api.create(body);
      toast.success(initial?.id ? 'Bijgewerkt' : 'Ticket aangemaakt'); onSaved(saved); onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{initial?.id ? 'Ticket bewerken' : 'Nieuw ticket'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField label="Titel" value={f.title} onChange={set('title')} size="small" autoFocus fullWidth />
          <TextField label="Omschrijving" value={f.description} onChange={set('description')} size="small" fullWidth multiline minRows={3} />
          <TextField label="Acceptatiecriteria (één per regel)" value={f.acceptance_criteria} onChange={set('acceptance_criteria')} size="small" fullWidth multiline minRows={2} />
          <Stack direction="row" spacing={2}>
            <TextField select label="Prioriteit" value={f.priority} onChange={set('priority')} size="small" sx={{ flex: 1 }}>
              {Object.entries(PRIO).map(([k, v]) => <MenuItem key={k} value={k}>{v.l}</MenuItem>)}
            </TextField>
            <TextField label="Toegewezen aan (email)" value={f.assignee} onChange={set('assignee')} size="small" sx={{ flex: 1 }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Branch" value={f.branch} onChange={set('branch')} size="small" sx={{ flex: 1 }} placeholder="feat/…" />
            <TextField label="Base" value={f.base_branch} onChange={set('base_branch')} size="small" sx={{ width: 120 }} />
          </Stack>
          <TextField label="Staging test-pad (optioneel)" value={f.test_path} onChange={set('test_path')} size="small" fullWidth placeholder="/backlog" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>{saving ? '…' : 'Opslaan'}</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ── detail dialog ── */
function Detail({ ticket, onClose, onChanged, onEdit }) {
  const [t, setT] = useState(ticket);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => { setT(ticket); if (ticket) api.comments(ticket.id).then(setComments).catch((e) => meldFout(e, 'Comments laden')); }, [ticket]);
  if (!t) return null;
  const run = async (label, fn) => {
    setBusy(label);
    try { const u = await fn(); if (u && u.id) { setT(u); onChanged(u); } toast.success('Gelukt'); }
    catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };
  const postComment = async () => {
    if (!newComment.trim()) return;
    try { const c = await api.addComment(t.id, newComment.trim()); setComments((cs) => [...cs, c]); setNewComment(''); onChanged({ ...t, comment_count: (t.comment_count || 0) + 1 }); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={!!t} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <span style={{ fontWeight: 700, flex: 1 }}>{t.title}</span>
        <Tooltip title="Bewerken"><IconButton onClick={() => onEdit(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 3 }}>
          <Box>
            {t.description && <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 14, mb: 2 }}>{t.description}</Typography>}
            {(t.acceptance_criteria || []).length > 0 && (
              <>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#868e96', textTransform: 'uppercase', mb: 0.5 }}>Acceptatiecriteria</Typography>
                <ul style={{ margin: '0 0 16px', paddingLeft: 18 }}>{t.acceptance_criteria.map((c, i) => <li key={i} style={{ fontSize: 13.5 }}>{c}</li>)}</ul>
              </>
            )}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#868e96', textTransform: 'uppercase', mb: 0.5 }}>Reacties</Typography>
            <Stack spacing={1} sx={{ mb: 1.5, maxHeight: 220, overflowY: 'auto' }}>
              {comments.map((c) => (
                <Box key={c.id} sx={{ bgcolor: '#f6f7f9', borderRadius: 1.5, p: 1 }}>
                  <Typography sx={{ fontSize: 11, color: '#868e96' }}>{c.author_email?.split('@')[0]} · {new Date(c.created_at).toLocaleString('nl-NL')}</Typography>
                  <Typography sx={{ fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{c.body}</Typography>
                </Box>
              ))}
              {!comments.length && <Typography sx={{ fontSize: 12.5, color: '#adb5bd' }}>Nog geen reacties.</Typography>}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField size="small" fullWidth placeholder="Reactie…" value={newComment} onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postComment(); } }} />
              <Button variant="outlined" size="small" onClick={postComment}>Plaats</Button>
            </Stack>
          </Box>
          {/* meta + GitHub panel */}
          <Box>
            <Stack spacing={0.75} sx={{ fontSize: 13, mb: 2 }}>
              <Row k="Status" v={COLUMNS.find((c) => c.key === t.status)?.label || t.status} />
              <Row k="Prioriteit" v={(PRIO[t.priority] || PRIO.med).l} />
              <Row k="Gevraagd door" v={t.requested_by} />
              <Row k="Toegewezen" v={t.assignee || '—'} />
              <Row k="Branch" v={t.branch || '—'} />
              <Row k="Base" v={t.base_branch} />
            </Stack>
            <Paper elevation={0} sx={{ border: '1px solid #e4e7ec', borderRadius: 2, p: 1.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#868e96', textTransform: 'uppercase', mb: 1 }}>GitHub</Typography>
              {t.pr_number ? (
                <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`PR #${t.pr_number} · ${t.pr_state}`} sx={{ bgcolor: prColor(t.pr_state) + '22', color: prColor(t.pr_state) }} />
                    <Chip size="small" label={`checks: ${t.pr_checks || 'none'}`} sx={{ bgcolor: prColor(t.pr_checks) + '22', color: prColor(t.pr_checks) }} />
                    <Chip size="small" label={`review: ${t.pr_reviews || 'none'}`} sx={{ bgcolor: prColor(t.pr_reviews) + '22', color: prColor(t.pr_reviews) }} />
                  </Stack>
                </Stack>
              ) : <Typography sx={{ fontSize: 12.5, color: '#adb5bd', mb: 1.5 }}>Nog geen PR.</Typography>}
              <Stack spacing={1}>
                {!t.pr_number && (
                  <Button size="small" variant="contained" disabled={!t.branch || busy === 'open'} onClick={() => run('open', () => api.openPr(t.id))}>
                    {busy === 'open' ? '…' : 'Open PR'}
                  </Button>
                )}
                {t.pr_number && (
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />} disabled={busy === 'refresh'} onClick={() => run('refresh', () => api.refreshPr(t.id))}>Ververs</Button>
                    <Button size="small" variant="contained" color="success" startIcon={<MergeIcon />}
                      disabled={t.pr_state !== 'open' || busy === 'merge'}
                      onClick={async () => {
                        if (!(await bevestig({ titel: 'PR mergen op GitHub?', tekst: `PR #${t.pr_number} wordt gemerged. Dit kan niet ongedaan worden gemaakt.`, knop: 'Mergen', gevaarlijk: true }))) return;
                        run('merge', () => api.mergePr(t.id));
                      }}>
                      {busy === 'merge' ? '…' : 'Merge'}
                    </Button>
                  </Stack>
                )}
                {t.pr_url && <Button size="small" startIcon={<OpenInNewIcon />} component={Link} href={t.pr_url} target="_blank">Op GitHub</Button>}
                <Button size="small" startIcon={<ScienceIcon />} onClick={() => window.open(STAGING + (t.test_path || '/'), '_blank')}>Test op staging</Button>
              </Stack>
            </Paper>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={async () => {
          if (!(await bevestig({ titel: 'Ticket verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
          try { await api.del(t.id); onChanged(null, t.id); onClose(); toast.success('Verwijderd'); } catch (e) { toast.error(e.message); }
        }}>Verwijderen</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Sluiten</Button>
      </DialogActions>
    </Dialog>
  );
}
const Row = ({ k, v }) => (
  <Stack direction="row" spacing={1}><Typography sx={{ fontSize: 12.5, color: '#868e96', minWidth: 108 }}>{k}</Typography><Typography sx={{ fontSize: 12.5, wordBreak: 'break-all' }}>{v}</Typography></Stack>
);

/* ── board (reusable: standalone page + Beheer-tab) ── */
export function BacklogBoard({ embedded = false }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    setLoading(true);
    try { setTickets(await api.list()); }
    catch (e) { toast.error('Kon backlog niet laden (admin/management vereist).'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upsert = (saved, deletedId) => {
    setTickets((ts) => {
      if (deletedId) return ts.filter((t) => t.id !== deletedId);
      if (!saved) return ts;
      const i = ts.findIndex((t) => t.id === saved.id);
      return i === -1 ? [saved, ...ts] : ts.map((t) => (t.id === saved.id ? saved : t));
    });
    if (detail && saved && detail.id === saved.id) setDetail(saved);
  };

  const verwijder = async (t) => {
    if (!(await bevestig({ titel: 'Feedback verwijderen?', tekst: t.title, knop: 'Verwijderen', gevaarlijk: true }))) return;
    try { await api.del(t.id); upsert(null, t.id); toast.success('Verwijderd'); }
    catch (e) { toast.error(e.message); }
  };

  const byCol = useMemo(() => {
    const m = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    for (const t of (Array.isArray(tickets) ? tickets : [])) (m[t.status] || m.backlog).push(t);
    return m;
  }, [tickets]);

  const onDragEnd = async ({ active, over }) => {
    if (!over) return;
    const id = Number(active.id);
    const newStatus = over.id;
    const t = tickets.find((x) => x.id === id);
    if (!t || t.status === newStatus || !COLUMNS.some((c) => c.key === newStatus)) return;
    const prev = t.status;
    setTickets((ts) => ts.map((x) => (x.id === id ? { ...x, status: newStatus } : x)));  // optimistic
    try { await api.update(id, { status: newStatus }); }
    catch (e) { toast.error(e.message); setTickets((ts) => ts.map((x) => (x.id === id ? { ...x, status: prev } : x))); }
  };

  const board = (
    <>
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>Ververs</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setFormOpen(true); }}>Nieuw ticket</Button>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', overflowX: 'auto', pb: 2 }}>
            {COLUMNS.map((col) => <Column key={col.key} col={col} tickets={byCol[col.key]} onOpen={setDetail} onDelete={verwijder} />)}
          </Box>
        </DndContext>
      )}
      <TicketForm open={formOpen} initial={editing} onClose={() => setFormOpen(false)} onSaved={upsert} />
      <Detail ticket={detail} onClose={() => setDetail(null)} onChanged={upsert}
        onEdit={(t) => { setDetail(null); setEditing(t); setFormOpen(true); }} />
    </>
  );

  // Embedded in the Beheer-panel: no own PageHeader (the panel supplies one).
  if (embedded) return <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>{board}</Box>;

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="view_kanban" title="Backlog" subtitle="Wat ik nu bouw — testen op staging, PR openen & mergen."
        accent={{ bg: '#e7dfff', color: '#6741d9' }} />
      {board}
    </Box>
  );
}

/* ── standalone page (deep-link /backlog) ── */
export default function Backlog() {
  return <BacklogBoard />;
}
